import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import Cookies from 'js-cookie';

interface Company {
  id: number;
  name: string;
  email?: string;
  city?: string;
}

interface ImpersonationContextType {
  impersonatedCompany: Company | null;
  isImpersonating: boolean;
  isDeveloper: boolean;
  startImpersonation: (company: Company) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

// Helper function to read impersonation state from cookies
const getImpersonationFromCookies = (): { company: Company | null; isDev: boolean } => {
  const role = Cookies.get('role');
  const isDevRole = role === 'DEVELOPER';

  const storedCompanyId = Cookies.get('impersonatedCompanyId');
  const storedCompanyName = Cookies.get('impersonatedCompanyName');

  if (storedCompanyId && storedCompanyName && isDevRole) {
    return {
      company: {
        id: parseInt(storedCompanyId, 10),
        name: storedCompanyName,
      },
      isDev: isDevRole,
    };
  }

  return { company: null, isDev: isDevRole };
};

export const ImpersonationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize state from cookies IMMEDIATELY during first render
  const [impersonatedCompany, setImpersonatedCompany] = useState<Company | null>(() => {
    const initial = getImpersonationFromCookies();
    return initial.company;
  });

  const [isDeveloper] = useState<boolean>(() => {
    const role = Cookies.get('role');
    return role === 'DEVELOPER';
  });


  const startImpersonation = (company: Company) => {
    // Store in cookies FIRST (before state update)
    Cookies.set('impersonatedCompanyId', company.id.toString(), { expires: 1, path: '/' }); // 1 day
    Cookies.set('impersonatedCompanyName', company.name, { expires: 1, path: '/' });

    // Store in state AFTER cookies
    setImpersonatedCompany(company);
  };

  const stopImpersonation = () => {
    // Clear state
    setImpersonatedCompany(null);

    // Clear cookies with explicit path
    Cookies.remove('impersonatedCompanyId', { path: '/' });
    Cookies.remove('impersonatedCompanyName', { path: '/' });

    // Reload to reset app state
    setTimeout(() => {
      window.location.href = '/';
    }, 300);
  };

  const value: ImpersonationContextType = useMemo(() => {
    const isImpersonating = impersonatedCompany !== null;
    return {
      impersonatedCompany,
      isImpersonating,
      isDeveloper,
      startImpersonation,
      stopImpersonation,
    };
  }, [impersonatedCompany, isDeveloper]);

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
};

export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};
